import { Checkbox, Field, Input, Textarea } from '@fluentui/react-components';
import { Button } from '../../../../Button';
import { Flex } from '../../../../Flex';
import { ServiceType } from '@mnspro-cloud/services';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveDialog } from '../../../../ResponsiveDialog/ResponsiveDialog';

export interface CreateServiceTypeDialogProps {
  isOpen: boolean;
  confirmed?: (serviceType: ServiceType) => void;
  cancelled?: () => void;
  editModel?: ServiceType;
}

export const CreateServiceTypeDialog: FC<
  CreateServiceTypeDialogProps
> = props => {
  const { isOpen, confirmed, cancelled, editModel } = props;
  const { t } = useTranslation();
  const [model, setModel] = useState<ServiceType>(
    editModel ?? {
      description: '',
      fullName: '',
      isActive: true,
      shortName: '',
    },
  );
  const handleActiveCheckboxChanged = (isActive: boolean) =>
    setModel({ ...model, isActive });
  const handleDescriptionChanged = (description: string) =>
    setModel({ ...model, description });
  const handleFullNameChanged = (fullName: string) =>
    setModel({ ...model, fullName });
  const handleShortNameChanged = (shortName: string) =>
    setModel({ ...model, shortName });
  const handleConfirmedClick = () => confirmed?.(model);
  const handleCancelledClick = () => cancelled?.();

  const isValid =
    model.description.length > 1 &&
    model.fullName.length > 1 &&
    model.shortName.length > 1;

  return (
    <ResponsiveDialog
      open={isOpen}
      header={
        editModel
          ? t('settings:classbook:Service_Type_Configuration:Dialog_Header_Text_Edit_Service_Type', { defaultValue: 'Diensttypen bearbeiten' })
          : t('settings:classbook:Service_Type_Configuration:Dialog_Header_Text_New_Service_Type', { defaultValue: 'Neuer Diensttyp' })
      }
      primaryAction={
        <Button
          appearance="primary"
          disabled={!isValid}
          onClick={handleConfirmedClick}
        >
          {t('Confirm', { defaultValue: 'Bestätigen' })}
        </Button>
      }
      secondaryAction={
        <Button
          onClick={handleCancelledClick}
        >
          {t('cancel', { defaultValue: 'Abbrechen' })}
        </Button>
      }
    >
      <Flex direction="column" gap="0.5rem">
        <Checkbox
          checked={model.isActive}
          onChange={(_, data) =>
            handleActiveCheckboxChanged(data.checked === true)
          }
          label={t('active', { defaultValue: 'Aktiv' })}
        />
        <Field
          label={t('settings:classbook:Service_Type_Configuration:Dialog_Please_Enter_Long_Name', { defaultValue: 'Bitte geben Sie einen Langnamen ein' })}
          required
          validationMessage={
            model.fullName.length < 1
              ? t('settings:classbook:Service_Type_Configuration:Dialog_Error_long_name_required', { defaultValue: 'Der Langname ist erforderlich' })
              : undefined
          }
          validationState={model.fullName.length < 1 ? 'error' : 'none'}
        >
          <Input
            appearance="filled-darker"
            value={model.fullName}
            onChange={(_, data) => handleFullNameChanged(data?.value ?? '')}
          />
        </Field>
        <Field
          label={t('settings:classbook:Service_Type_Configuration:Dialog_Please_Enter_Short_Name', { defaultValue: 'Bitte geben Sie einen Kurznamen ein' })}
          required
          validationMessage={
            model.shortName.length < 1
              ? t('settings:classbook:Service_Type_Configuration:Dialog_Error_short_name_required', { defaultValue: 'Der Kurzname ist erforderlich' })
              : undefined
          }
          validationState={model.shortName.length < 1 ? 'error' : 'none'}
        >
          <Input
            appearance="filled-darker"
            value={model.shortName}
            onChange={(_, data) => handleShortNameChanged(data?.value ?? '')}
          />
        </Field>
        <Field
          label={t('settings:classbook:Service_Type_Configuration:Dialog_Please_Enter_Short_Description', { defaultValue: 'Bitte geben Sie eine kurze Beschreibung ein' })}
          required
          validationMessage={
            model.description.length < 1
              ? t('settings:classbook:Service_Type_Configuration:Dialog_Error_description_required', { defaultValue: 'Die Beschreibung ist erforderlich' })
              : undefined
          }
          validationState={model.description.length < 1 ? 'error' : 'none'}
        >
          <Textarea
            appearance="filled-darker"
            value={model.description}
            onChange={(_, data) => handleDescriptionChanged(data?.value ?? '')}
          />
        </Field>
      </Flex>
    </ResponsiveDialog>
  );
};
